import { useCallback, useEffect, useRef, type ReactNode } from "react";
import { BuilddocsIframe } from "@features/builddocs-iframe";
import { useIframeStore } from "@features/builddocs-iframe/model";
import { env } from "@shared/config.ts";

const messageRegEx = /^(\w+):(\w+)(?::(.+))?$/;

export function LegacyIframeProvider({
  children,
}: {
  children: ReactNode;
}) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const shellRef = useRef<HTMLDivElement>(null);
  const setIframeRef = useIframeStore((state) => state.setIframeRef);
  const size = useIframeStore((state) => state.size);
  const isIframeInitialized = useIframeStore(
    (state) => state.isIframeInitialized,
  );
  const setIsIframeInitialized = useIframeStore(
    (state) => state.setIsIframeInitialized,
  );
  const isVisible = useIframeStore((state) => state.isVisible);

  useEffect(() => {
    setIframeRef(iframeRef);
  }, [setIframeRef]);

  useEffect(() => {
    const shell = shellRef.current;
    if (!shell) return;

    shell.style.display = isVisible ? "" : "none";
    shell.style.width = size?.inlineSize ? `${size.inlineSize}px` : "";
    shell.style.height = size?.blockSize ? `${size.blockSize}px` : "";
  }, [isVisible, size]);

  const onMessage = useCallback(
    (e: MessageEvent<any>) => {
      if (
        !iframeRef ||
        typeof e.data !== "string" ||
        !messageRegEx.test(e.data)
      )
        return;

      const [, namespace, action] = e.data.match(messageRegEx) ?? [];

      if (namespace !== "builddocs" || e.origin !== env.OLD_FRONTEND_URL)
        return;

      switch (action) {
        case "ready": {
          if (isIframeInitialized !== 2) setIsIframeInitialized();
          break;
        }
        case "unready": {
          break;
        }
        default: {
          break;
        }
      }
    },
    [isIframeInitialized, setIsIframeInitialized],
  );

  useEffect(() => {
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [onMessage]);

  return (
    <>
      {children}
      <div ref={shellRef} className="builddocs-iframe-shell">
        <BuilddocsIframe ref={iframeRef} />
      </div>
    </>
  );
}
